import React from "react";
import { MessageCircle } from "lucide-react";
import { buildWhatsAppLink } from "../lib/api";

/**
 * Reusable WhatsApp click-to-chat button.
 * Variants: "floating" (fixed FAB) | "inline" (regular button) | "compact" (small).
 *
 * @param {string} message - Pre-filled chat message (default: general inquiry)
 * @param {string} trackingId - When provided, switches to tracking-context message
 */
export const WhatsAppButton = ({ variant = "floating", message, trackingId, label = "Chat on WhatsApp", testid = "whatsapp-btn" }) => {
  const text = message || (
    trackingId
      ? `Hello Hayti Bon Sèvis Shipping, I have a question regarding shipment tracking number ${trackingId}.`
      : "Hello Hayti Bon Sèvis Shipping, I would like to inquire about a shipment."
  );
  const href = buildWhatsAppLink(text);

  if (variant === "floating") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        aria-label="WhatsApp chat"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#20BC59] text-white rounded-full shadow-2xl px-5 py-3.5 transition-all hover:scale-105"
        style={{ boxShadow: "0 10px 30px rgba(37,211,102,0.45)" }}
      >
        <WhatsAppIcon size={22} />
        <span className="hidden sm:inline text-sm font-medium tracking-wide">{label}</span>
      </a>
    );
  }

  if (variant === "compact") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-[#25D366] hover:text-[#20BC59] text-sm"
      >
        <WhatsAppIcon size={14} /> {label}
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BC59] text-white rounded-sm px-5 py-3 text-xs uppercase tracking-widest font-medium transition-colors"
    >
      <WhatsAppIcon size={16} /> {label}
    </a>
  );
};

// Inline SVG of WhatsApp logo (clean, sized via prop)
const WhatsAppIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 01-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 01-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.846 0 2.793-1.39 2.793-2.236 0-.114-.015-.215-.043-.315-.115-.43-1.146-.516-1.534-.502M16.46 30c-2.193 0-4.358-.6-6.219-1.732l-.43-.258-4.484 1.175 1.203-4.354-.273-.43A12.116 12.116 0 014.357 16.5C4.4 9.842 9.815 4.4 16.474 4.4c3.225 0 6.262 1.246 8.555 3.554a12.087 12.087 0 013.546 8.546c-.014 6.658-5.429 12.1-12.115 12.1m10.32-22.42C24.043 4.842 20.428 3.5 16.46 3.5c-8.122 0-14.74 6.582-14.74 14.677 0 2.595.687 5.132 1.992 7.367L1.5 33.5l8.158-2.138a14.755 14.755 0 006.788 1.717h.014c8.122 0 14.726-6.59 14.726-14.677a14.66 14.66 0 00-4.32-10.408" />
  </svg>
);

export default WhatsAppButton;
